"use client";

import { X } from "lucide-react";
import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils/cn";
import { IconButton } from "./button";

export type ModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
  className?: string;
};

const sizeClass = { sm: "max-w-md", md: "max-w-lg", lg: "max-w-2xl" } as const;

export function Modal({ open, onClose, title, description, children, footer, size = "md", className }: ModalProps) {
  const titleId = useId();
  const descriptionId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    previousFocus.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() => {
      panelRef.current?.querySelector<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')?.focus();
    });
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onCloseRef.current();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocus.current?.focus();
    };
  }, [open]);

  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto p-ds-3 sm:p-ds-4" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-surface-canvas/80 backdrop-blur-sm transition-opacity duration-base"
        aria-label="Close dialog"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        className={cn(
          "relative z-10 flex max-h-[calc(100dvh-1.5rem)] w-full flex-col overflow-hidden rounded-card border border-card-border bg-surface-elevated text-foreground shadow-overlay sm:max-h-[calc(100dvh-2rem)]",
          sizeClass[size],
          className,
        )}
      >
        <div className="flex shrink-0 items-start justify-between gap-ds-4 border-b border-card-border bg-surface-secondary px-ds-4 py-ds-4 sm:px-ds-6">
          <div className="min-w-0">
            <h2 id={titleId} className="text-title text-foreground">{title}</h2>
            {description ? <p id={descriptionId} className="mt-ds-1 text-caption text-muted">{description}</p> : null}
          </div>
          <IconButton variant="ghost" aria-label="Close dialog" onClick={onClose}><X className="h-4 w-4" /></IconButton>
        </div>
        {children !== undefined && children !== null && children !== false ? (
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-ds-4 py-ds-4 sm:px-ds-6 sm:py-ds-5">{children}</div>
        ) : null}
        {footer ? <div className="shrink-0 border-t border-card-border bg-surface-secondary px-ds-4 py-ds-4 sm:px-ds-6">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
